import type { datadogRum, RumErrorEvent } from '@datadog/browser-rum';

type BeforeSend = NonNullable<
  Parameters<typeof datadogRum.init>[0]['beforeSend']
>;

export const getErrorName = (event: RumErrorEvent) => {
  if (event.error?.message?.toLowerCase().includes('csp')) {
    // CSP itself is not a type, we pass it for the Sentry beforeSend
    return 'CSP';
  } else if (event.error?.type) {
    return event.error?.type;
  } else {
    return 'Error';
  }
};

/**
 * Generate an error object that is compatible with the
 * existing `beforeSend` function we have for Sentry.
 */
export const createSentryError: (
  ...args: Parameters<BeforeSend>
) => SentryExtendedError = (event, context: any) => {
  if (event.type !== 'error') {
    return new Error('Not an error event');
  }

  const error = event.error;
  const errorName = getErrorName(event);

  let errorCode: number | undefined;
  let errorStatusCode: number | undefined;
  if (context?.error instanceof Error) {
    errorCode = (context.error as SentryExtendedError).code;
    errorStatusCode = (context.error as SentryExtendedError).statusCode;
  }

  return new SentryExtendedError(error.message, {
    name: errorName,
    code: errorCode,
    statusCode: errorStatusCode,
  });
};

export class SentryExtendedError extends Error {
  code?: number;
  statusCode?: number;

  constructor(
    message: string,
    {
      name,
      code,
      statusCode,
    }: {
      name?: string;
      code?: number | undefined;
      statusCode?: number | undefined;
    },
  ) {
    super(message);

    if (name) {
      this.name = name;
    }

    if (code) {
      this.code = code;
    }

    if (statusCode) {
      this.statusCode = statusCode;
    }
  }
}
